import styled, { css, keyframes } from 'styled-components';

import { rem } from '../../utils';

export const StyledOverlayWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.Color.OverlayFixed.Default};
  border-radius: inherit;
  opacity: 0;
  transition: all ${({ theme }) => theme.Transition.Timing.Out};
  cursor: pointer;
  z-index: 10;
`;

export const placeholderShimmerKeyframe = (size: number) => keyframes`
  0% {
    background-position: ${rem(size * -1)} 0;
  }
  100% {
    background-position: ${rem(size)} 0;
  }
`;

export const StyledWrapper = styled.div<{
  type?: string;
  isImageLoaded?: boolean;
}>`
  position: relative;
  display: inline-flex;
  min-height: ${({ theme, type }) =>
    type === 'text' ? 'auto' : rem(theme.Card.Size.Inline.height)};
  cursor: pointer;
  user-select: none;
  border-radius: ${({ theme }) => rem(theme.Border.Radius.Medium)};
  background: ${({ theme }) => theme.Color.NeutralsFixed.Border};

  ${({ type, theme }) =>
    type === 'text' &&
    css`
      width: 100%;
      max-width: ${({ theme }) => rem(theme.Card.Size.Full.width)};
      background-color: ${theme.Components.Panel.Background};
      box-shadow: ${({ theme }) => `inset 0 0 0 1px ${theme.Color.Neutrals.Border}`};
      flex-direction: column;
      outline: none;
      &:hover {
        box-shadow: ${({ theme }) => `inset 0 0 0 1.5px ${theme.Color.Brand.Primary}`};
        transition: box-shadow ${theme.Transition.Timing.In};
      }
    `}

  &:hover {
    ${StyledOverlayWrapper} {
      opacity: 1;
      transition: all ${({ theme }) => theme.Transition.Timing.In};
    }
  }
`;

export const StyledImage = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: ${({ theme }) => rem(theme.Card.Size.Inline.height)};
  object-fit: cover;
  border-radius: inherit;

  img {
    display: block;
  }
`;

export const StyleContentWrapper = styled.div<{
  type?: string;
  variant?: string;
}>`
  display: inline-flex;
  flex-direction: column;
  align-items: ${({ variant }) => (variant === 'centered' ? 'center' : 'flex-start')};
  justify-content: space-between;
  text-align: left;
  width: 100%;
  height: 100%;
  padding: ${({ type, theme }) =>
    rem(type === 'text' ? theme.Spacing.Size.Small : theme.Spacing.Size.XSmall)};
  overflow: hidden;
`;

export const StyledButtonWrapper = styled.div`
  display: inline-flex;
  justify-content: flex-end;
  gap: ${({ theme }) => rem(theme.Spacing.Size.XXSmall)};
  width: 100%;
`;

export const StyledCardButton = styled.a<{ buttonStyle: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => rem(theme.Sizes.X3Large)};
  height: ${({ theme }) => rem(theme.Sizes.X3Large)};
  cursor: pointer;
  transition: all ${({ theme }) => theme.Transition.Timing.Out};

  ${({ buttonStyle, theme }) =>
    buttonStyle === 'text'
      ? css`
          padding: ${rem(theme.Card.Spacing.XXSmall)};
          background-color: transparent;
          color: ${theme.Color.Neutrals.Dark};
          fill: ${theme.Color.Neutrals.Dark};
          border-radius: ${rem(theme.Border.Radius.Small)};

          &:hover {
            transition: background-color ${({ theme }) => theme.Transition.Timing.In};
            background-color: ${({ theme }) => theme.Color.Neutrals.Light};
          }
        `
      : css`
          padding: ${rem(theme.Card.Spacing.XXSmall)};
          background-color: ${theme.Color.OverlayFixed.Default};
          border-radius: ${rem(theme.Border.Radius.Small)};
          color: ${theme.Color.Neutrals.Paper};
          fill: ${theme.Color.Neutrals.Paper};

          &:hover {
            transition: background-color ${({ theme }) => theme.Transition.Timing.In};
            background-color: ${({ theme }) => theme.Color.OverlayFixed.Hover};
          }
        `}
`;

export const StyledTextActionButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => rem(theme.Spacing.Size.XXSmall)};
  width: calc(100% - ${({ theme }) => rem(theme.Spacing.Size.Small * 2)});
  margin: ${({ theme }) =>
    `0 ${rem(theme.Spacing.Size.Small)} ${rem(theme.Spacing.Size.Small)} ${rem(
      theme.Spacing.Size.Small
    )}`};
  padding-top: ${({ theme }) => rem(theme.Spacing.Size.XSmall)};
  border-top: 1px solid ${({ theme }) => theme.Color.Neutrals.Border};
`;

export const StyledTextLabelWrapper = styled.div`
  display: inline-flex;
  align-items: flex-start;
  flex: 1;
  max-width: 75%;
`;

export const StyledTextWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => rem(theme.Spacing.Size.XSmall)};
`;
